// Sharing into Bloom from another app (Android's share sheet; the Web Share Target API). The installed web app's
// manifest names Bloom as a share target; the service worker (public/sw.js) takes the POST, keeps the shared files
// and text in the Cache Storage under SHARE_CACHE, and sends the app to /share. The app reads them once (readShare),
// asks where they go (the quick switcher titled "Send to…", in the last server), and the chosen conversation's
// composer takes them: the files as uploads, the text in the box. Nothing is sent until the person sends it.

/** The Cache Storage bucket the service worker keeps a share in (one at a time; a new share replaces it). */
export const SHARE_CACHE = "bloom-share";
/** What the service worker stores: the text parts, and how many files follow at SHARE_FILE + index. */
export const SHARE_META = "/share-pending/meta";
export const SHARE_FILE = "/share-pending/file/";
/** Files one share can bring: the composer's own limit. */
export const SHARE_MAX_FILES = 10;

export interface SharedText {
  title?: string;
  text?: string;
  url?: string;
}

export interface Share {
  /** What goes in the message box ("" for files alone). */
  text: string;
  files: File[];
}

/**
 * The message text a share makes: its text and its link, each once (apps often put the link inside the text too, or
 * send only a title). The title goes in only when there's nothing else, since apps fill it with the page's name.
 */
export function shareText({ title, text, url }: SharedText): string {
  const t = (text ?? "").trim();
  const u = (url ?? "").trim();
  const parts = [t];
  if (u && !t.includes(u)) parts.push(u);
  const out = parts.filter(Boolean).join("\n");
  return out || (title ?? "").trim();
}

/** Reads the share the service worker kept, and forgets it there (it's read once). Null when there's none. */
export async function readShare(): Promise<Share | null> {
  if (typeof caches === "undefined") return null;
  try {
    return await readKept();
  } catch {
    return null;
  } finally {
    // Read once, even when reading failed partway: a broken share mustn't come back on every /share.
    await caches.delete(SHARE_CACHE).catch(() => {});
  }
}

let reading: Promise<Share | null> | null = null;
/**
 * readShare, once per page: /share's effect may run again (the session refreshes from its cached copy on a cold
 * start) and a second read would find the share already gone.
 */
export function readShareOnce(): Promise<Share | null> {
  reading ??= readShare();
  return reading;
}

async function readKept(): Promise<Share | null> {
  const cache = await caches.open(SHARE_CACHE);
  const meta = await cache.match(SHARE_META);
  if (!meta) return null;
  const m = (await meta.json()) as SharedText & { files?: number };
  const files: File[] = [];
  for (let i = 0; i < Math.min(m.files ?? 0, SHARE_MAX_FILES); i++) {
    const r = await cache.match(SHARE_FILE + i);
    if (!r) continue;
    const name = decodeURIComponent(r.headers.get("x-name") ?? `shared-${i + 1}`);
    const blob = await r.blob();
    files.push(new File([blob], name, { type: blob.type || r.headers.get("content-type") || "application/octet-stream" }));
  }
  const share = { text: shareText(m), files };
  return share.text || share.files.length ? share : null;
}

// ---- The share waiting for a conversation (in memory: it never outlives this page) ----

let pending: Share | null = null;
let target: string | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((fn) => fn());

/** One line saying what a share holds: "2 photos and a video", "a file", or the start of its text. */
export function describeShare(share: Share): string {
  const n = { photo: 0, video: 0, file: 0 };
  for (const f of share.files) n[f.type.startsWith("image/") ? "photo" : f.type.startsWith("video/") ? "video" : "file"]++;
  const words = (["photo", "video", "file"] as const).filter((k) => n[k]).map((k) => (n[k] === 1 ? `a ${k}` : `${n[k]} ${k}s`));
  const files = words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words.at(-1)}` : (words[0] ?? "");
  const text = share.text.replace(/\s+/g, " ").trim();
  if (files && text) return `${files[0]!.toUpperCase()}${files.slice(1)}: ${text}`;
  return files ? `${files[0]!.toUpperCase()}${files.slice(1)}` : text;
}

/** The share waiting for a pick, if any (for its description). */
export const waitingShare = (): Share | null => (pending && !target ? pending : null);

/** A share read and waiting for the person to pick where it goes. */
export function holdShare(share: Share) {
  pending = share;
  target = null;
  changed();
}

/** Is a share waiting for a conversation to be picked? */
export const shareWaiting = (): boolean => !!pending && !target;

/** The person picked where it goes: that conversation's composer takes it (takeShareFor). */
export function sendShareTo(channelId: string) {
  if (!pending) return;
  target = channelId;
  changed();
}

/** The person closed the picker without choosing: the share is dropped. */
export function dropShare() {
  pending = null;
  target = null;
  changed();
}

/** The composer for `channelId` takes the share sent its way (once). */
export function takeShareFor(channelId: string): Share | null {
  if (!pending || target !== channelId) return null;
  const s = pending;
  pending = null;
  target = null;
  changed();
  return s;
}

export function onShareChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}
